/**
 * post-V9 Section 0 Iter 3 — compact stores for complex primitives:
 * Hatch, BlockInsert, ArrayRect, Viewport.
 *
 * Hatch boundaryPaths is packed into typed-array form via
 * `compactHatchBoundaryBinary` (Opt 6 — Phase 1b). BlockInsert ATTRIB
 * attributes remain JSON-serialized for the same reason as
 * Multileader.leaderRoots — deeply nested structures with high
 * variance per entity.
 */
import type {
  ArrayRectEntity,
  BlockInsertEntity,
  DrawingPoint,
  DrawingPoint3D,
  HatchEntity,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import {
  decodeBoundaryBinary,
  encodeBoundaryBinary,
  type HatchBoundaryBinary,
} from "./compactHatchBoundaryBinary";
import {
  internString,
  lookupString,
  type SharedStringTable,
} from "./sharedStringTable";

/**
 * Empty-boundary sentinel returned by `encodeBoundaryBinary([])`.
 * Stored at unused slots inside the dense `boundaryPathsBinary` array so
 * the slot has a valid (zero-edge, zero-path) record even before its
 * entity is written. Shared singleton — never mutated.
 */
const EMPTY_BOUNDARY_BINARY: HatchBoundaryBinary = Object.freeze({
  pathFlags: new Uint8Array(0),
  pathEdgeOffsets: new Uint32Array(1), // single zero — "0 edges total"
  edgeTypes: new Uint8Array(0),
  lineParams: new Float32Array(0),
  arcParams: new Float32Array(0),
  ellipticArcParams: new Float32Array(0),
  lineOffsets: new Uint32Array(0),
  arcOffsets: new Uint32Array(0),
  ellipticArcOffsets: new Uint32Array(0),
}) as HatchBoundaryBinary;

/* ── HATCH ── */

const HATCH_FLOATS = 3;
const HATCH_INTS = 4;
const FLAG_HATCH_SOLID = 1;
const FLAG_HATCH_ASSOC = 2;
const FLAG_HATCH_DBL = 4;

export interface CompactHatchStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  flags: Uint8Array;
  /**
   * Dense, slot-indexed binary boundary-path records (Opt 6 — Phase 1b).
   * Slot `i` holds the binary form of entity `i`'s `boundaryPaths`.
   * Empty slots (< count) carry a shared zero-record sentinel; unused
   * slots (>= count, up to capacity) also reference the sentinel.
   */
  boundaryPathsBinary: HatchBoundaryBinary[];
  seedPointsJson: Map<number, string>;
  // PDF-6.5a: gradient fill is optional + variable-length (colors[]); persisted
  // as JSON per entity (mirrors seedPointsJson) so an imported gradient HATCH
  // survives the compact cache → snapshot → export path without dropping the
  // 450..470 block.
  gradientJson: Map<number, string>;
  annotativeContextsJson: Map<number, string>;
  creationScaleNames: Map<number, string>;
  // Batch-3 HATCH fidelity fields (S-HATCH-ELEVATION-XY / S-HATCH-PIXELSIZE /
  // S-HATCH-EXTRUSION / S-HATCH-ASSOC-REFS). Each is OPTIONAL on the model
  // (absent ⇒ a sensible default), so a sparse Map (entry only when present)
  // preserves the absent-vs-present distinction with zero per-entity cost for
  // the common default-hatch case. `extrusion` mirrors CompactBlockInsertStore's
  // `extrusions` Map. `sourceBoundaryHandles` is per-path variable-length string
  // data the binary boundary record does NOT carry — persisted as JSON
  // (string[][], path-indexed) and re-attached on decode, mirroring seedPointsJson.
  elevationXY: Map<number, { x: number; y: number }>;
  pixelSizes: Map<number, number>;
  extrusions: Map<number, DrawingPoint3D>;
  sourceBoundaryHandlesJson: Map<number, string>;
  // S-HATCH-CUSTOM — custom pattern-definition lines (variable-length,
  // structured per-line). Persisted as JSON per entity (mirrors gradientJson /
  // seedPointsJson) so a custom-pattern HATCH survives the compact cache →
  // snapshot → export path without dropping the 78/53/43/44/45/46/79/49 block.
  // Sparse: an entry only when the model carries ≥1 line. doubleLine is already
  // round-tripped via FLAG_HATCH_DBL above (no separate column needed).
  customPatternLinesJson: Map<number, string>;
}

export function createCompactHatchStore(
  initialCapacity = 128,
): CompactHatchStore {
  const cap = Math.max(1, initialCapacity);
  const boundaryPathsBinary = new Array<HatchBoundaryBinary>(cap);
  for (let k = 0; k < cap; k++) boundaryPathsBinary[k] = EMPTY_BOUNDARY_BINARY;
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * HATCH_FLOATS),
    ints: new Uint16Array(cap * HATCH_INTS),
    flags: new Uint8Array(cap),
    boundaryPathsBinary,
    seedPointsJson: new Map(),
    gradientJson: new Map(),
    annotativeContextsJson: new Map(),
    creationScaleNames: new Map(),
    elevationXY: new Map(),
    pixelSizes: new Map(),
    extrusions: new Map(),
    sourceBoundaryHandlesJson: new Map(),
    customPatternLinesJson: new Map(),
  };
}

function ensureHatchCap(s: CompactHatchStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const f = new Float32Array(c * HATCH_FLOATS); f.set(s.floats);
  const i = new Uint16Array(c * HATCH_INTS); i.set(s.ints);
  const fl = new Uint8Array(c); fl.set(s.flags);
  s.floats = f; s.ints = i; s.flags = fl;
  const oldLen = s.boundaryPathsBinary.length;
  s.boundaryPathsBinary.length = c;
  for (let k = oldLen; k < c; k++) s.boundaryPathsBinary[k] = EMPTY_BOUNDARY_BINARY;
}

export function appendHatch(
  s: CompactHatchStore,
  table: SharedStringTable,
  e: HatchEntity,
): number {
  const i = s.count;
  ensureHatchCap(s, i + 1);
  s.count++;
  const f = i * HATCH_FLOATS;
  s.floats[f + 0] = e.patternAngle;
  s.floats[f + 1] = e.patternScale;
  s.floats[f + 2] = e.elevation;
  const ii = i * HATCH_INTS;
  s.ints[ii + 0] = internString(table, e.patternName);
  s.ints[ii + 1] = e.hatchStyle & 0xffff;
  s.ints[ii + 2] = e.patternType & 0xffff;
  let fl = 0;
  if (e.solidFill) fl |= FLAG_HATCH_SOLID;
  if (e.associative) fl |= FLAG_HATCH_ASSOC;
  if (e.doubleLine) fl |= FLAG_HATCH_DBL;
  s.flags[i] = fl;
  writeBase(s.base, table, i, e);
  s.boundaryPathsBinary[i] = encodeBoundaryBinary(e.boundaryPaths);
  if (e.seedPoints.length > 0) {
    s.seedPointsJson.set(i, JSON.stringify(e.seedPoints));
  }
  if (e.gradient !== undefined) {
    s.gradientJson.set(i, JSON.stringify(e.gradient));
  }
  if (e.annotativeContexts.length > 0) {
    s.annotativeContextsJson.set(i, JSON.stringify(e.annotativeContexts));
  }
  if (e.creationScaleName !== undefined) {
    s.creationScaleNames.set(i, e.creationScaleName);
  }
  // Batch-3 HATCH fidelity fields — persisted sparsely (entry only when the
  // model carries the optional field) so default hatches cost nothing extra.
  if (e.elevationX !== undefined || e.elevationY !== undefined) {
    s.elevationXY.set(i, { x: e.elevationX ?? 0, y: e.elevationY ?? 0 });
  }
  if (e.pixelSize !== undefined) {
    s.pixelSizes.set(i, e.pixelSize);
  }
  // Omit the identity extrusion (0,0,1) to match the bridge export/import
  // paths — an identity normal must round-trip as `undefined`, not a stored
  // value, so a compact-backed hatch stays consistent with the string path.
  if (
    e.extrusion !== undefined &&
    !(e.extrusion.x === 0 && e.extrusion.y === 0 && e.extrusion.z === 1)
  ) {
    s.extrusions.set(i, { ...e.extrusion });
  }
  // S-HATCH-ASSOC-REFS — per-path associative source-boundary handles. The
  // binary boundary record carries edges/pathFlags ONLY, so persist the handle
  // arrays separately as a path-indexed string[][] (sparse entries dropped to
  // null) when ANY path carries handles.
  const handlesByPath = e.boundaryPaths.map((p) => p.sourceBoundaryHandles);
  if (handlesByPath.some((h) => h !== undefined && h.length > 0)) {
    s.sourceBoundaryHandlesJson.set(
      i,
      JSON.stringify(handlesByPath.map((h) => h ?? null)),
    );
  }
  // S-HATCH-CUSTOM — custom pattern-definition lines (sparse JSON, set only when
  // the model carries ≥1 line so default/predefined hatches cost nothing).
  if (e.customPatternLines !== undefined && e.customPatternLines.length > 0) {
    s.customPatternLinesJson.set(i, JSON.stringify(e.customPatternLines));
  }
  return i;
}

export function hatchFromCompact(
  s: CompactHatchStore,
  table: SharedStringTable,
  i: number,
): HatchEntity {
  if (i < 0 || i >= s.count) {
    throw new RangeError(
      `hatchFromCompact: index ${i} out of range (count=${s.count})`,
    );
  }
  const f = i * HATCH_FLOATS;
  const ii = i * HATCH_INTS;
  const fl = s.flags[i] ?? 0;
  const bpBin = s.boundaryPathsBinary[i] ?? EMPTY_BOUNDARY_BINARY;
  const boundaryPaths: HatchEntity["boundaryPaths"] = decodeBoundaryBinary(bpBin);
  let seedPoints: HatchEntity["seedPoints"] = [];
  const spJson = s.seedPointsJson.get(i);
  if (spJson !== undefined) {
    try { seedPoints = JSON.parse(spJson); } catch { seedPoints = []; }
  }
  let annotativeContexts: HatchEntity["annotativeContexts"] = [];
  const annoJson = s.annotativeContextsJson.get(i);
  if (annoJson !== undefined) {
    try { annotativeContexts = JSON.parse(annoJson); } catch { annotativeContexts = []; }
  }
  const result: HatchEntity = {
    ...readBase(s.base, table, i),
    type: "Hatch",
    patternName: lookupString(table, s.ints[ii + 0]!),
    solidFill: (fl & FLAG_HATCH_SOLID) !== 0,
    associative: (fl & FLAG_HATCH_ASSOC) !== 0,
    hatchStyle: s.ints[ii + 1]!,
    patternType: s.ints[ii + 2]!,
    patternAngle: s.floats[f + 0]!,
    patternScale: s.floats[f + 1]!,
    doubleLine: (fl & FLAG_HATCH_DBL) !== 0,
    boundaryPaths,
    elevation: s.floats[f + 2]!,
    seedPoints,
    annotativeContexts,
  };
  const csn = s.creationScaleNames.get(i);
  if (csn !== undefined) result.creationScaleName = csn;
  // PDF-6.5a: restore the optional gradient fill (JSON) so an imported gradient
  // HATCH re-materialised from the compact cache keeps its 450..470 block.
  const gJson = s.gradientJson.get(i);
  if (gJson !== undefined) {
    try {
      result.gradient = JSON.parse(gJson) as HatchEntity["gradient"];
    } catch {
      /* corrupt JSON → leave gradient undefined rather than throw */
    }
  }
  // Batch-3 HATCH fidelity fields — restore each only when the store carried it
  // (absent ⇒ the model field stays undefined, matching the source).
  const exy = s.elevationXY.get(i);
  if (exy !== undefined) {
    result.elevationX = exy.x;
    result.elevationY = exy.y;
  }
  const px = s.pixelSizes.get(i);
  if (px !== undefined) result.pixelSize = px;
  const ext = s.extrusions.get(i);
  if (ext !== undefined) result.extrusion = { ...ext };
  // S-HATCH-ASSOC-REFS — re-attach per-path source-boundary handles (path-
  // indexed string[][]). Decoded boundaryPaths preserve path order, so the
  // index aligns; a null slot means that path had no handles.
  const sbhJson = s.sourceBoundaryHandlesJson.get(i);
  if (sbhJson !== undefined) {
    try {
      const perPath = JSON.parse(sbhJson) as Array<string[] | null>;
      for (let p = 0; p < boundaryPaths.length; p += 1) {
        const handles = perPath[p];
        if (handles && handles.length > 0) {
          boundaryPaths[p]!.sourceBoundaryHandles = handles;
        }
      }
    } catch {
      /* corrupt JSON → leave handles unset rather than throw */
    }
  }
  // S-HATCH-CUSTOM — restore custom pattern-definition lines (JSON) only when
  // the store carried them (absent ⇒ the model field stays undefined).
  const cplJson = s.customPatternLinesJson.get(i);
  if (cplJson !== undefined) {
    try {
      result.customPatternLines = JSON.parse(
        cplJson,
      ) as HatchEntity["customPatternLines"];
    } catch {
      /* corrupt JSON → leave customPatternLines undefined rather than throw */
    }
  }
  return result;
}

export function estimateHatchBytesPerEntity(
  s: CompactHatchStore,
): number {
  // Fixed: floats column + ints column + flag byte + ~8B object-header
  // overhead for the boundary-binary record reference + base columns.
  const fixed = HATCH_FLOATS * 4 + HATCH_INTS * 2 + 1 + 8 + BASE_BYTES_PER_ENTITY;
  if (s.count === 0) return fixed;
  // Binary cost per entity — sum each typed array's byteLength.
  let binaryBytes = 0;
  for (let i = 0; i < s.count; i++) {
    const bin = s.boundaryPathsBinary[i] ?? EMPTY_BOUNDARY_BINARY;
    binaryBytes +=
      bin.pathFlags.byteLength +
      bin.pathEdgeOffsets.byteLength +
      bin.edgeTypes.byteLength +
      bin.lineParams.byteLength +
      bin.arcParams.byteLength +
      bin.ellipticArcParams.byteLength +
      bin.lineOffsets.byteLength +
      bin.arcOffsets.byteLength +
      bin.ellipticArcOffsets.byteLength;
  }
  return fixed + binaryBytes / s.count;
}

/* ── BLOCK INSERT ── */

const BI_FLOATS = 8;
const BI_INTS = 4;
const DEFAULT_3D: DrawingPoint3D = { x: 0, y: 0, z: 1 };

function isDefault3DZ(p: DrawingPoint3D | undefined): boolean {
  if (!p) return true;
  return p.x === 0 && p.y === 0 && p.z === 1;
}

export interface CompactBlockInsertStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  extrusions: Map<number, DrawingPoint3D>;
  attributesJson: Map<number, string>;
  customPropertiesJson: Map<number, string>;
  visibilityStates: Map<number, string>;
  annotationScalesJson: Map<number, string>;
  representationOverridesJson: Map<number, string>;
  annotativeContextsJson: Map<number, string>;
  creationScaleNames: Map<number, string>;
  // ── T9b review round 1 — the terminating SEQEND's own handle (DXF group 5),
  //    presence-keyed ─────────────────────────────────────────────────────
  // `BlockInsertEntity.seqendHandle` is the handle an IMPORTED INSERT+ATTRIB
  // sequence's SEQEND carried; the export re-emits it as `SEQEND 5 <h>`.
  // Without a compact-store slot the product path (import → compact store →
  // export) dropped it, so the re-exported SEQEND was handle-less (silently
  // re-minted by ezdxf, recovered by AutoCAD). Sparse + presence-keyed (same
  // convention as `sourceBlockNames` on the array store): an entry exists ONLY
  // when the entity carried the field, so absence reads back absent (never
  // default-materialized — a from-scratch INSERT has none until the conformant
  // export mints one).
  seqendHandles: Map<number, string>;
}

export function createCompactBlockInsertStore(
  initialCapacity = 256,
): CompactBlockInsertStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * BI_FLOATS),
    ints: new Uint16Array(cap * BI_INTS),
    extrusions: new Map(),
    attributesJson: new Map(),
    customPropertiesJson: new Map(),
    visibilityStates: new Map(),
    annotationScalesJson: new Map(),
    representationOverridesJson: new Map(),
    annotativeContextsJson: new Map(),
    creationScaleNames: new Map(),
    seqendHandles: new Map(),
  };
}

function ensureBiCap(s: CompactBlockInsertStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const f = new Float32Array(c * BI_FLOATS); f.set(s.floats);
  const i = new Uint16Array(c * BI_INTS); i.set(s.ints);
  s.floats = f; s.ints = i;
}

export function appendBlockInsert(
  s: CompactBlockInsertStore,
  table: SharedStringTable,
  e: BlockInsertEntity,
): number {
  const i = s.count;
  ensureBiCap(s, i + 1);
  s.count++;
  const f = i * BI_FLOATS;
  s.floats[f + 0] = e.insertionPoint.x;
  s.floats[f + 1] = e.insertionPoint.y;
  s.floats[f + 2] = e.scaleX;
  s.floats[f + 3] = e.scaleY;
  s.floats[f + 4] = e.scaleZ;
  s.floats[f + 5] = e.rotation;
  s.floats[f + 6] = e.columnSpacing;
  s.floats[f + 7] = e.rowSpacing;
  const ii = i * BI_INTS;
  s.ints[ii + 0] = internString(table, e.blockName);
  s.ints[ii + 2] = e.columnCount & 0xffff;
  s.ints[ii + 3] = e.rowCount & 0xffff;
  writeBase(s.base, table, i, e);
  if (!isDefault3DZ(e.extrusion)) s.extrusions.set(i, { ...e.extrusion });
  if (e.attributes.length > 0) s.attributesJson.set(i, JSON.stringify(e.attributes));
  if (Object.keys(e.customProperties).length > 0) {
    s.customPropertiesJson.set(i, JSON.stringify(e.customProperties));
  }
  if (e.visibilityState !== undefined) s.visibilityStates.set(i, e.visibilityState);
  if (e.annotationScales.length > 0) {
    s.annotationScalesJson.set(i, JSON.stringify(e.annotationScales));
  }
  if (e.representationOverrides !== undefined) {
    s.representationOverridesJson.set(i, JSON.stringify(e.representationOverrides));
  }
  if (e.annotativeContexts.length > 0) {
    s.annotativeContextsJson.set(i, JSON.stringify(e.annotativeContexts));
  }
  if (e.creationScaleName !== undefined) s.creationScaleNames.set(i, e.creationScaleName);
  // Presence-keyed: store the SEQEND handle ONLY when the entity carried it.
  if (e.seqendHandle !== undefined) s.seqendHandles.set(i, e.seqendHandle);
  return i;
}

export function blockInsertFromCompact(
  s: CompactBlockInsertStore,
  table: SharedStringTable,
  i: number,
): BlockInsertEntity {
  if (i < 0 || i >= s.count) {
    throw new RangeError(
      `blockInsertFromCompact: index ${i} out of range (count=${s.count})`,
    );
  }
  const f = i * BI_FLOATS;
  const ii = i * BI_INTS;
  const decode = <T>(json: string | undefined, fallback: T): T => {
    if (json === undefined) return fallback;
    try { return JSON.parse(json) as T; } catch { return fallback; }
  };
  const result: BlockInsertEntity = {
    ...readBase(s.base, table, i),
    type: "Block",
    blockName: lookupString(table, s.ints[ii + 0]!),
    insertionPoint: { x: s.floats[f + 0]!, y: s.floats[f + 1]! },
    scaleX: s.floats[f + 2]!,
    scaleY: s.floats[f + 3]!,
    scaleZ: s.floats[f + 4]!,
    rotation: s.floats[f + 5]!,
    columnCount: s.ints[ii + 2]!,
    rowCount: s.ints[ii + 3]!,
    columnSpacing: s.floats[f + 6]!,
    rowSpacing: s.floats[f + 7]!,
    extrusion: s.extrusions.get(i) ?? { ...DEFAULT_3D },
    attributes: decode(s.attributesJson.get(i), [] as BlockInsertEntity["attributes"]),
    customProperties: decode(
      s.customPropertiesJson.get(i),
      {} as BlockInsertEntity["customProperties"],
    ),
    annotationScales: decode(
      s.annotationScalesJson.get(i),
      [] as BlockInsertEntity["annotationScales"],
    ),
    annotativeContexts: decode(
      s.annotativeContextsJson.get(i),
      [] as BlockInsertEntity["annotativeContexts"],
    ),
  };
  const vis = s.visibilityStates.get(i);
  if (vis !== undefined) result.visibilityState = vis;
  const ro = s.representationOverridesJson.get(i);
  if (ro !== undefined) {
    try { result.representationOverrides = JSON.parse(ro); } catch { /* ignore */ }
  }
  const csn = s.creationScaleNames.get(i);
  if (csn !== undefined) result.creationScaleName = csn;
  // T9b review round 1 — restore presence-keyed: set ONLY when the entry
  // exists so an INSERT without a source SEQEND handle stays absent.
  const seqend = s.seqendHandles.get(i);
  if (seqend !== undefined) result.seqendHandle = seqend;
  return result;
}

export function estimateBlockInsertBytesPerEntity(): number {
  return BI_FLOATS * 4 + BI_INTS * 2 + BASE_BYTES_PER_ENTITY + 8;
}

/* ── ARRAY RECT ── */

const AR_FLOATS = 7;
const AR_INTS = 2;

export interface CompactArrayRectStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  flags: Uint8Array;
  sourceEntityIdsJson: Map<number, string>;
  // ── S-ARRAYRECT-ITEMS-DECODE — persisted per-item records (sparse,
  //    presence-keyed) ──────────────────────────────────────────────
  // `ArrayRectEntity.assocItems` carried through the compact round-trip the
  // same way as the other sparse JSON payloads (blockInsert attributesJson
  // convention): an entry exists ONLY when the model entity carried the
  // field, so absence reads back absent (never default-materialized to []).
  // The write-back-critical association metadata is carried separately below
  // so compact rehydration does not erase the import-time dirty-diff state.
  /** JSON-encoded `ArrayAssocItem[]` (assocItems), presence-keyed. */
  assocItemsJson: Map<number, string>;
  /** Presence-keyed metadata required by associative-array write-back. */
  assocMetadataJson: Map<number, string>;
  // ── SL-18 — source block-reference name (DXF group 2), presence-keyed ──
  // `ArrayRectEntity.sourceBlockName` is REQUIRED for the ARRAYRECT→MINSERT
  // export path (a NON-associative grid re-emits as a genuine MINSERT of this
  // block — dxfBridge.arrayMinsert). Without a compact-store slot, any property/
  // edit path that rehydrates an imported array through this cache and writes it
  // back DROPS the field, so a later export sees no source block and falls back
  // to the grid-losing `0 ARRAYRECT` path SL-18 exists to avoid (@codex SL-18
  // round-3 P2). Sparse + presence-keyed (same convention as sourceEntityIdsJson
  // / assocItemsJson): an entry exists ONLY when the entity carried the field,
  // so absence reads back absent (never default-materialized).
  sourceBlockNames: Map<number, string>;
}

export function createCompactArrayRectStore(
  initialCapacity = 64,
): CompactArrayRectStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * AR_FLOATS),
    ints: new Uint16Array(cap * AR_INTS),
    flags: new Uint8Array(cap),
    sourceEntityIdsJson: new Map(),
    assocItemsJson: new Map(),
    assocMetadataJson: new Map(),
    sourceBlockNames: new Map(),
  };
}

function ensureArCap(s: CompactArrayRectStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const f = new Float32Array(c * AR_FLOATS); f.set(s.floats);
  const i = new Uint16Array(c * AR_INTS); i.set(s.ints);
  const fl = new Uint8Array(c); fl.set(s.flags);
  s.floats = f; s.ints = i; s.flags = fl;
}

export function appendArrayRect(
  s: CompactArrayRectStore,
  table: SharedStringTable,
  e: ArrayRectEntity,
): number {
  const i = s.count;
  ensureArCap(s, i + 1);
  s.count++;
  const f = i * AR_FLOATS;
  s.floats[f + 0] = e.basePoint.x;
  s.floats[f + 1] = e.basePoint.y;
  s.floats[f + 2] = e.rowSpacing;
  s.floats[f + 3] = e.columnSpacing;
  s.floats[f + 4] = e.rowTotal;
  s.floats[f + 5] = e.columnTotal;
  s.floats[f + 6] = e.axisAngle;
  const ii = i * AR_INTS;
  s.ints[ii + 0] = e.rowCount & 0xffff;
  s.ints[ii + 1] = e.columnCount & 0xffff;
  s.flags[i] = e.associative ? 1 : 0;
  writeBase(s.base, table, i, e);
  if (e.sourceEntityIds.length > 0) {
    s.sourceEntityIdsJson.set(i, JSON.stringify(e.sourceEntityIds));
  }
  // Presence = field present on the entity (NOT length > 0) — an explicit
  // `assocItems: []` must restore as `[]`, never collapse to absent (same
  // convention as gradientJson / secondaryContextsJson / brightness).
  if (e.assocItems !== undefined) {
    s.assocItemsJson.set(i, JSON.stringify(e.assocItems));
  }
  const assocMetadata: Pick<
    ArrayRectEntity,
    "assocParamsSnapshot" | "assocHandles" | "axisAngleFromNamedForm"
  > = {};
  if (e.assocParamsSnapshot !== undefined) {
    assocMetadata.assocParamsSnapshot = e.assocParamsSnapshot;
  }
  if (e.assocHandles !== undefined) {
    assocMetadata.assocHandles = e.assocHandles;
  }
  if (e.axisAngleFromNamedForm !== undefined) {
    assocMetadata.axisAngleFromNamedForm = e.axisAngleFromNamedForm;
  }
  if (Object.keys(assocMetadata).length > 0) {
    s.assocMetadataJson.set(i, JSON.stringify(assocMetadata));
  }
  // Presence-keyed like sourceEntityIdsJson/assocItemsJson: store the raw block
  // name ONLY when the entity carried it, so a reducer-built array with no
  // source block reads back absent (never default-materialized). Preserves the
  // ARRAYRECT→MINSERT source-block reference across a compact rehydrate/writeback
  // (@codex SL-18 round-3 P2).
  if (e.sourceBlockName !== undefined) {
    s.sourceBlockNames.set(i, e.sourceBlockName);
  }
  return i;
}

export function arrayRectFromCompact(
  s: CompactArrayRectStore,
  table: SharedStringTable,
  i: number,
): ArrayRectEntity {
  if (i < 0 || i >= s.count) {
    throw new RangeError(
      `arrayRectFromCompact: index ${i} out of range (count=${s.count})`,
    );
  }
  const f = i * AR_FLOATS;
  const ii = i * AR_INTS;
  const idsJson = s.sourceEntityIdsJson.get(i);
  let sourceEntityIds: string[] = [];
  if (idsJson !== undefined) {
    try { sourceEntityIds = JSON.parse(idsJson); } catch { sourceEntityIds = []; }
  }
  const result: ArrayRectEntity = {
    ...readBase(s.base, table, i),
    type: "Rectangular Associative Array",
    basePoint: { x: s.floats[f + 0]!, y: s.floats[f + 1]! },
    rowCount: s.ints[ii + 0]!,
    columnCount: s.ints[ii + 1]!,
    rowSpacing: s.floats[f + 2]!,
    columnSpacing: s.floats[f + 3]!,
    rowTotal: s.floats[f + 4]!,
    columnTotal: s.floats[f + 5]!,
    axisAngle: s.floats[f + 6]!,
    associative: (s.flags[i] ?? 0) !== 0,
    sourceEntityIds,
  };
  // SL-18 — restore the source block name presence-keyed: set ONLY when the
  // entry exists so a reducer-built array with no source block stays absent
  // (absence-preserving sparse convention). Keeps the ARRAYRECT→MINSERT source
  // reference intact through a compact rehydrate/writeback (@codex SL-18 R3 P2).
  const srcBlock = s.sourceBlockNames.get(i);
  if (srcBlock !== undefined) {
    result.sourceBlockName = srcBlock;
  }
  // Presence-keyed: set ONLY when the entry exists so absence stays absent
  // (S-ARRAYRECT-ITEMS-DECODE — absence-preserving sparse convention).
  const itemsJson = s.assocItemsJson.get(i);
  if (itemsJson !== undefined) {
    try {
      result.assocItems = JSON.parse(itemsJson) as NonNullable<
        ArrayRectEntity["assocItems"]
      >;
    } catch { /* corrupt JSON — leave absent */ }
  }
  const metadataJson = s.assocMetadataJson.get(i);
  if (metadataJson !== undefined) {
    try {
      const metadata = JSON.parse(metadataJson) as Pick<
        ArrayRectEntity,
        "assocParamsSnapshot" | "assocHandles" | "axisAngleFromNamedForm"
      >;
      if (metadata.assocParamsSnapshot !== undefined) {
        result.assocParamsSnapshot = metadata.assocParamsSnapshot;
      }
      if (metadata.assocHandles !== undefined) {
        result.assocHandles = metadata.assocHandles;
      }
      if (Object.prototype.hasOwnProperty.call(metadata, "axisAngleFromNamedForm")) {
        result.axisAngleFromNamedForm = metadata.axisAngleFromNamedForm;
      }
    } catch { /* corrupt JSON — leave metadata absent */ }
  }
  return result;
}

export function estimateArrayRectBytesPerEntity(): number {
  return AR_FLOATS * 4 + AR_INTS * 2 + 1 + BASE_BYTES_PER_ENTITY;
}

/* ── VIEWPORT ── */

const VP_FLOATS = 12;
const VP_INTS = 1;
const DEFAULT_VIEW_DIR: DrawingPoint3D = { x: 0, y: 0, z: 1 };
const DEFAULT_VIEW_TARGET: DrawingPoint3D = { x: 0, y: 0, z: 0 };

export interface CompactViewportStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  flags: Uint8Array;
  viewDirections: Map<number, DrawingPoint3D>;
  viewTargets: Map<number, DrawingPoint3D>;
  frozenLayersJson: Map<number, string>;
  cannoscaleNames: Map<number, string>;
  cannoscaleFactors: Map<number, number>;
  annoAllVisible: Map<number, boolean>;
  // ── Per-VP override fields (EDIT_DWG_VP_OVERRIDES XDATA source) — sparse,
  // presence-keyed (S15 Slice F). The authoritative in-session scale/ucs a
  // viewport-scale / lock / view edit persists on the TYPED entity fields
  // (`vpOverrideScale` {num,denom}, `vpOverrideUcsName`). Without a compact-store
  // slot they were DROPPED on every AggregateStore round-trip, silently degrading
  // an authored custom scale (e.g. 2:3) back to the lossy viewHeight/height
  // re-derive on rehydration. Set only when present so absence reads back absent.
  /** authoritative in-session scale ({num,denom}) — DXF export carrier. */
  vpOverrideScale: Map<number, { num: number; denom: number }>;
  /** per-VP UCS name override (EDIT_DWG_VP_OVERRIDES XDATA). */
  vpOverrideUcsName: Map<number, string>;
  /** DXF group-69 sequential viewport id (1 = paper-space control VP). */
  viewportIds: Map<number, number>;
  /** DXF group-90 status bit flags (bit 0x4000 = display-locked). */
  statusBitFlagsMap: Map<number, number>;
  // ── Batch-5 snap/grid + shade-plot (sparse — default-omit) ──────────
  // Stored as sparse maps rather than dense float slots to keep the
  // existing positional float layout (slots 0..10) stable and avoid a
  // binary-format bump. Each map is populated only when the field is set.
  /** DXF 13/23 — snap base point. */
  snapBase: Map<number, DrawingPoint>;
  /** DXF 14/24 — snap X/Y spacing. */
  snapSpacing: Map<number, DrawingPoint>;
  /** DXF 15/25 — grid X/Y spacing. */
  gridSpacing: Map<number, DrawingPoint>;
  /** DXF 50 — snap/grid rotation angle (radians). */
  snapRotation: Map<number, number>;
  /** DXF 61 — major grid line frequency (int). */
  gridMajor: Map<number, number>;
  /** DXF 72 — circle zoom / smoothness percent. */
  circleZoomPercent: Map<number, number>;
  /** DXF 170 — shade-plot mode (plot-only). */
  shadePlotMode: Map<number, number>;
  /** DXF 281 — render mode (plot-only). */
  renderMode: Map<number, number>;
  // ── S-VIEWPORT-A lighting block (#304 r3) — sparse, presence-keyed ──
  // DXF 141/142 projected onto ViewportEntity.brightness/contrast by the
  // bridge. Same convention as the Batch-5 maps above: an entry exists
  // only when the model entity carried the field, so absence reads back
  // absent AND a genuine 0.0 (the real-AutoCAD lighting-block default,
  // `141 0.0` / `142 0.0`) stays distinguishable from "no lighting block".
  /** DXF 141 — view brightness (lighting block). */
  brightness: Map<number, number>;
  /** DXF 142 — view contrast (lighting block). */
  contrast: Map<number, number>;
  // ── S-VIEWPORT-CLIP-HANDLE (PR #318) — sparse, presence-keyed ──────────
  // DXF group 340: the native clipping-boundary soft-pointer handle carried
  // verbatim on ViewportEntity.clippingBoundaryHandle. Stored as a sparse
  // string map (same convention as `cannoscaleNames` above) so a clipped
  // viewport survives streaming/compaction round-trip through the
  // AggregateStore: an entry exists ONLY when the model entity carried the
  // field, so absence reads back absent and a literal "0" (= no clip) stays
  // distinguishable from "no 340 group at all".
  /** DXF 340 — clipping-boundary handle. */
  clippingBoundaryHandle: Map<number, string>;
  // ── S-VIEWPORT-CLIP-RESOLVE (EPIC-VIEWPORT-CLIP slice 3) — sparse ───────────
  // The two RESOLVED typed projections produced by the OBJECTS second-pass
  // (`applyViewportClipAndMembership`): `clipPath` (resolved WCS clip polygon)
  // and `childEntityIds` (model-space membership index). NOT raw DXF groups —
  // they are derived object-graphs computed at import time. They must survive
  // the compact-cache round-trip because the read-through wrapper
  // (`wrapSpaceAsReadThrough`) re-materialises `space.entities` from
  // `viewportFromCompact`, so a viewport reconstructed without these fields
  // would lose its clip boundary + membership end-to-end on EVERY file-import
  // path (streaming + non-streaming). Stored as sparse JSON maps (same
  // presence-keyed convention as `clippingBoundaryHandle`): an entry exists
  // ONLY when the model entity carried the field, so absence reads back
  // undefined (no resolved clip / membership not computed).
  /** Resolved WCS clip polygon (`ViewportEntity.clipPath`), JSON-encoded. */
  clipPathJson: Map<number, string>;
  /** Membership index (`ViewportEntity.childEntityIds`), JSON-encoded. */
  childEntityIdsJson: Map<number, string>;
  // ── S-VIEWPORT-CLIP-RESOLVE (Codex #326 r5) — sparse, presence-keyed ─────────
  // `clipBoundaryPaperSpace` — the RETAINED paper-space clip boundary polygon for
  // native-340 VPCLIP clips. `clipPath` is the DERIVED model-space projection of
  // this boundary through the viewport's current view transform; on every view
  // change (applyViewportSetView / applyViewportSetScale) `clipPath` is recomputed
  // from this paper-space source. So this field MUST survive the compact-cache
  // round-trip too — without it, a viewport re-materialised via
  // `viewportFromCompact` (read-through wrapper / updateCachedEntityInPlace) loses
  // the paper boundary and the pan/zoom recompute can no longer run, freezing the
  // old model-space `clipPath`. Same presence-keyed convention as `clipPathJson`:
  // an entry exists ONLY when the model entity carried the field, so absence reads
  // back undefined (SPATIAL_FILTER + rectangular viewports carry no paper boundary).
  /**
   * Retained paper-space clip boundary (`ViewportEntity.clipBoundaryPaperSpace`),
   * JSON-encoded. Present only for native-340 clips.
   */
  clipBoundaryPaperSpaceJson: Map<number, string>;
  // ── S-VIEWPORT-CLIP-RESOLVE (@codex #496 P2) — sparse, presence-keyed ─────────
  // `clipBoundaryKind` — the SOURCE-preserved KIND of a resolved SPATIAL_FILTER
  // (XCLIP/VPCLIP) clip boundary ("rectangular" | "polygonal"). It is the ONLY
  // signal that distinguishes a polygon SPATIAL_FILTER clip whose group-90
  // non-rect bit is 0 (some producers omit it) from a rectangular clip, so the
  // ViewportInspector clip-mode row keys on it. Without a compact-store slot the
  // read-through rematerialisation (`viewportFromCompact`) dropped it, so a
  // compact-cache/large-drawing viewport re-read as `clipBoundaryKind: undefined`
  // and the inspector mislabelled such polygon clips as Rectangular. Same
  // presence-keyed convention as the other clip maps: an entry exists ONLY when
  // the model entity carried the field (imported SPATIAL_FILTER clips; native-340
  // + rect + authored clips leave it undefined).
  /** DXF-derived SPATIAL_FILTER clip kind (`ViewportEntity.clipBoundaryKind`). */
  clipBoundaryKind: Map<number, "rectangular" | "polygonal">;
}

export function createCompactViewportStore(
  initialCapacity = 64,
): CompactViewportStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * VP_FLOATS),
    ints: new Uint16Array(cap * VP_INTS),
    flags: new Uint8Array(cap),
    viewDirections: new Map(),
    viewTargets: new Map(),
    frozenLayersJson: new Map(),
    cannoscaleNames: new Map(),
    cannoscaleFactors: new Map(),
    vpOverrideScale: new Map(),
    vpOverrideUcsName: new Map(),
    annoAllVisible: new Map(),
    viewportIds: new Map(),
    statusBitFlagsMap: new Map(),
    snapBase: new Map(),
    snapSpacing: new Map(),
    gridSpacing: new Map(),
    snapRotation: new Map(),
    gridMajor: new Map(),
    circleZoomPercent: new Map(),
    shadePlotMode: new Map(),
    renderMode: new Map(),
    brightness: new Map(),
    contrast: new Map(),
    clippingBoundaryHandle: new Map(),
    clipPathJson: new Map(),
    childEntityIdsJson: new Map(),
    clipBoundaryPaperSpaceJson: new Map(),
    clipBoundaryKind: new Map(),
  };
}

function ensureVpCap(s: CompactViewportStore, n: number): void {
  if (n <= s.base.capacity) return;
  const c = growBaseColumns(s.base, n);
  const f = new Float32Array(c * VP_FLOATS); f.set(s.floats);
  const i = new Uint16Array(c * VP_INTS); i.set(s.ints);
  const fl = new Uint8Array(c); fl.set(s.flags);
  s.floats = f; s.ints = i; s.flags = fl;
}

export function appendViewport(
  s: CompactViewportStore,
  table: SharedStringTable,
  e: ViewportEntity,
): number {
  const i = s.count;
  ensureVpCap(s, i + 1);
  s.count++;
  const f = i * VP_FLOATS;
  s.floats[f + 0] = e.center.x;
  s.floats[f + 1] = e.center.y;
  s.floats[f + 2] = e.width;
  s.floats[f + 3] = e.height;
  s.floats[f + 4] = e.viewCenter.x;
  s.floats[f + 5] = e.viewCenter.y;
  s.floats[f + 6] = e.viewHeight;
  s.floats[f + 7] = e.viewTwistAngle;
  s.floats[f + 8] = e.lensLength;
  s.floats[f + 9] = e.frontClipZ;
  s.floats[f + 10] = e.backClipZ;
  const ii = i * VP_INTS;
  s.ints[ii + 0] = e.viewportStatus & 0xffff;
  s.flags[i] = e.displayLocked ? 1 : 0;
  writeBase(s.base, table, i, e);
  if (e.viewDirection.x !== 0 || e.viewDirection.y !== 0 || e.viewDirection.z !== 1) {
    s.viewDirections.set(i, { ...e.viewDirection });
  }
  if (e.viewTarget.x !== 0 || e.viewTarget.y !== 0 || e.viewTarget.z !== 0) {
    s.viewTargets.set(i, { ...e.viewTarget });
  }
  if (e.frozenLayers.length > 0) {
    s.frozenLayersJson.set(i, JSON.stringify(e.frozenLayers));
  }
  if (e.viewportCannoscaleName !== undefined) {
    s.cannoscaleNames.set(i, e.viewportCannoscaleName);
  }
  if (e.viewportCannoscaleFactor !== undefined) {
    s.cannoscaleFactors.set(i, e.viewportCannoscaleFactor);
  }
  // S15 Slice F — persist the authoritative in-session scale/ucs override so a
  // viewport-scale / view / lock edit (setViewportScaleEntity's `vpOverrideScale`)
  // survives the AggregateStore compact round-trip instead of being silently
  // dropped to the lossy viewHeight/height re-derive.
  //
  // KNOWN PRE-EXISTING LIMITATION (orthogonal to this slice — panel edits never
  // touch these): the native handle fields `frozenLayerHandles` (DXF group 331) +
  // `layerOverrideHandles` (335/343/344/91 — model.ts:1597/1603) still have NO
  // compact-store column, so they read back undefined on a streamed/compact-backed
  // drawing. Documented at captureSnapshot.ts (the read-through capture that omits
  // them). Adding columns for those is a separate follow-up (tracked by the
  // coordinator), NOT introduced here.
  //
  // INHERITS the same limitation (@codex #514 P2 "Persist source 331 handles
  // through compact storage"): `sourceFrozenLayerHandles` (viewportFrozenLayers.ts
  // — the FULL verbatim native-331 order/casing snapshot export prefers over the
  // reduced `frozenLayerHandles`) is ALSO not columned here. For a compact/
  // streamed-backed import, `viewportFromCompact` therefore rematerialises a
  // viewport with `sourceFrozenLayerHandles: undefined`, and export correctly
  // falls back to the reduced `frozenLayerHandles` — the frozen-layer SET stays
  // fully correct, only the byte-order/casing fidelity improvement is unavailable
  // for this storage path, exactly like its sibling native-handle fields above.
  // Add a `sourceFrozenLayerHandlesJson` column in the SAME dedicated follow-up
  // that adds `frozenLayerHandles`/`layerOverrideHandles` columns, not piecemeal.
  if (e.vpOverrideScale !== undefined) {
    s.vpOverrideScale.set(i, { num: e.vpOverrideScale.num, denom: e.vpOverrideScale.denom });
  }
  if (e.vpOverrideUcsName !== undefined) {
    s.vpOverrideUcsName.set(i, e.vpOverrideUcsName);
  }
  if (e.viewportAnnoAllVisible !== undefined) {
    s.annoAllVisible.set(i, e.viewportAnnoAllVisible);
  }
  if (e.viewportId !== undefined) {
    s.viewportIds.set(i, e.viewportId);
  }
  if (e.statusBitFlags !== undefined) {
    s.statusBitFlagsMap.set(i, e.statusBitFlags);
  }
  // Batch-5 snap/grid + shade-plot (sparse — set only when present).
  if (e.snapBase !== undefined) s.snapBase.set(i, { ...e.snapBase });
  if (e.snapSpacing !== undefined) s.snapSpacing.set(i, { ...e.snapSpacing });
  if (e.gridSpacing !== undefined) s.gridSpacing.set(i, { ...e.gridSpacing });
  if (e.snapRotation !== undefined) s.snapRotation.set(i, e.snapRotation);
  if (e.gridMajor !== undefined) s.gridMajor.set(i, e.gridMajor);
  if (e.circleZoomPercent !== undefined) s.circleZoomPercent.set(i, e.circleZoomPercent);
  if (e.shadePlotMode !== undefined) s.shadePlotMode.set(i, e.shadePlotMode);
  if (e.renderMode !== undefined) s.renderMode.set(i, e.renderMode);
  // S-VIEWPORT-A lighting block (#304 r3) — set only when present so a
  // streamed/virtualised round-trip through the AggregateStore neither drops
  // an imported 141/142 nor invents one for viewports without the block.
  if (e.brightness !== undefined) s.brightness.set(i, e.brightness);
  if (e.contrast !== undefined) s.contrast.set(i, e.contrast);
  // S-VIEWPORT-CLIP-HANDLE (PR #318) — set only when present so a clipped
  // viewport's group-340 handle survives streaming/compaction (absence stays
  // absent; a literal "0" = no clip is preserved verbatim).
  if (e.clippingBoundaryHandle !== undefined) {
    s.clippingBoundaryHandle.set(i, e.clippingBoundaryHandle);
  }
  // S-VIEWPORT-CLIP-RESOLVE (slice 3) — persist the resolved clip polygon +
  // membership so they survive the compact-cache round-trip the read-through
  // wrapper performs on every file-import path. Set only when present so a
  // viewport with no resolved clip / membership reads back undefined.
  if (e.clipPath !== undefined) {
    s.clipPathJson.set(i, JSON.stringify(e.clipPath));
  }
  if (e.childEntityIds !== undefined) {
    s.childEntityIdsJson.set(i, JSON.stringify(e.childEntityIds));
  }
  // S-VIEWPORT-CLIP-RESOLVE (Codex #326 r5) — persist the retained paper-space
  // clip boundary so the view-change recompute (which re-projects clipPath from
  // this source) still works after a compact-cache rematerialisation. Set only
  // when present — native-340 clips carry it; SPATIAL_FILTER + rect viewports do
  // not, so absence reads back undefined.
  if (e.clipBoundaryPaperSpace !== undefined) {
    s.clipBoundaryPaperSpaceJson.set(i, JSON.stringify(e.clipBoundaryPaperSpace));
  }
  // S-VIEWPORT-CLIP-RESOLVE (@codex #496 P2) — persist the SOURCE-preserved
  // SPATIAL_FILTER clip kind so the inspector's clip-mode row still distinguishes
  // a polygon clip (non-rect bit 0) from a rectangular one after a compact-cache
  // rematerialisation. Set only when present — imported SPATIAL_FILTER clips carry
  // it; native-340 + rect + authored viewports do not, so absence reads back
  // undefined.
  if (e.clipBoundaryKind !== undefined) {
    s.clipBoundaryKind.set(i, e.clipBoundaryKind);
  }
  return i;
}

export function viewportFromCompact(
  s: CompactViewportStore,
  table: SharedStringTable,
  i: number,
): ViewportEntity {
  if (i < 0 || i >= s.count) {
    throw new RangeError(
      `viewportFromCompact: index ${i} out of range (count=${s.count})`,
    );
  }
  const f = i * VP_FLOATS;
  const ii = i * VP_INTS;
  let frozenLayers: string[] = [];
  const flJson = s.frozenLayersJson.get(i);
  if (flJson !== undefined) {
    try { frozenLayers = JSON.parse(flJson); } catch { frozenLayers = []; }
  }
  const result: ViewportEntity = {
    ...readBase(s.base, table, i),
    type: "Viewport",
    center: { x: s.floats[f + 0]!, y: s.floats[f + 1]! },
    width: s.floats[f + 2]!,
    height: s.floats[f + 3]!,
    viewCenter: { x: s.floats[f + 4]!, y: s.floats[f + 5]! },
    viewHeight: s.floats[f + 6]!,
    viewDirection: s.viewDirections.get(i) ?? { ...DEFAULT_VIEW_DIR },
    viewTarget: s.viewTargets.get(i) ?? { ...DEFAULT_VIEW_TARGET },
    viewTwistAngle: s.floats[f + 7]!,
    lensLength: s.floats[f + 8]!,
    frontClipZ: s.floats[f + 9]!,
    backClipZ: s.floats[f + 10]!,
    viewportStatus: s.ints[ii + 0]!,
    displayLocked: (s.flags[i] ?? 0) !== 0,
    frozenLayers,
  };
  const cn = s.cannoscaleNames.get(i);
  if (cn !== undefined) result.viewportCannoscaleName = cn;
  const cf = s.cannoscaleFactors.get(i);
  if (cf !== undefined) result.viewportCannoscaleFactor = cf;
  const vos = s.vpOverrideScale.get(i);
  if (vos !== undefined) result.vpOverrideScale = { num: vos.num, denom: vos.denom };
  const vun = s.vpOverrideUcsName.get(i);
  if (vun !== undefined) result.vpOverrideUcsName = vun;
  const av = s.annoAllVisible.get(i);
  if (av !== undefined) result.viewportAnnoAllVisible = av;
  const vid = s.viewportIds.get(i);
  if (vid !== undefined) result.viewportId = vid;
  const sbf = s.statusBitFlagsMap.get(i);
  if (sbf !== undefined) result.statusBitFlags = sbf;
  // Batch-5 snap/grid + shade-plot (sparse — undefined when absent).
  const sb = s.snapBase.get(i);
  if (sb !== undefined) result.snapBase = { ...sb };
  const ss = s.snapSpacing.get(i);
  if (ss !== undefined) result.snapSpacing = { ...ss };
  const gs = s.gridSpacing.get(i);
  if (gs !== undefined) result.gridSpacing = { ...gs };
  const sr = s.snapRotation.get(i);
  if (sr !== undefined) result.snapRotation = sr;
  const gm = s.gridMajor.get(i);
  if (gm !== undefined) result.gridMajor = gm;
  const czp = s.circleZoomPercent.get(i);
  if (czp !== undefined) result.circleZoomPercent = czp;
  const spm = s.shadePlotMode.get(i);
  if (spm !== undefined) result.shadePlotMode = spm;
  const rm = s.renderMode.get(i);
  if (rm !== undefined) result.renderMode = rm;
  // S-VIEWPORT-A lighting block (#304 r3) — undefined when absent.
  const br = s.brightness.get(i);
  if (br !== undefined) result.brightness = br;
  const ct = s.contrast.get(i);
  if (ct !== undefined) result.contrast = ct;
  // S-VIEWPORT-CLIP-HANDLE (PR #318) — undefined when absent.
  const cbh = s.clippingBoundaryHandle.get(i);
  if (cbh !== undefined) result.clippingBoundaryHandle = cbh;
  // S-VIEWPORT-CLIP-RESOLVE (slice 3) — restore the resolved clip polygon +
  // membership. Undefined when absent (no resolved clip / membership).
  const cpJson = s.clipPathJson.get(i);
  if (cpJson !== undefined) {
    try {
      result.clipPath = JSON.parse(cpJson) as ViewportEntity["clipPath"];
    } catch {
      // Malformed payload (should never happen — we wrote it) ⇒ leave absent.
    }
  }
  const ceJson = s.childEntityIdsJson.get(i);
  if (ceJson !== undefined) {
    try {
      result.childEntityIds = JSON.parse(
        ceJson,
      ) as ViewportEntity["childEntityIds"];
    } catch {
      // Malformed payload ⇒ leave absent.
    }
  }
  // S-VIEWPORT-CLIP-RESOLVE (Codex #326 r5) — restore the retained paper-space
  // clip boundary. Undefined when absent (no native-340 clip / no paper boundary).
  const cbpJson = s.clipBoundaryPaperSpaceJson.get(i);
  if (cbpJson !== undefined) {
    try {
      result.clipBoundaryPaperSpace = JSON.parse(
        cbpJson,
      ) as ViewportEntity["clipBoundaryPaperSpace"];
    } catch {
      // Malformed payload ⇒ leave absent.
    }
  }
  // S-VIEWPORT-CLIP-RESOLVE (@codex #496 P2) — restore the SOURCE-preserved
  // SPATIAL_FILTER clip kind. Undefined when absent (native-340 / rect / authored).
  const cbk = s.clipBoundaryKind.get(i);
  if (cbk !== undefined) result.clipBoundaryKind = cbk;
  return result;
}

export function estimateViewportBytesPerEntity(): number {
  return VP_FLOATS * 4 + VP_INTS * 2 + 1 + BASE_BYTES_PER_ENTITY;
}
